/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createContext, RefObject, useContext } from 'react';

import { useAppSelector } from '../../../../app/hooks';
import { selectPageByDocumentVersionAndTitleWithChanges } from '../chatSlice';
import { MessageType, PageType } from '../../algorithm/types';

/** Not exported because don't use these directly,
 *  but use the helpers below, which return the type without the undefined
 *  and throw an error if the context is not set.
 */
const MessageContext = createContext<MessageType | undefined>(undefined);
const MessageIndexContext = createContext<number | undefined>(undefined);
export const FhirScopeContext = createContext<{ scopeId: string; scopeRef: RefObject<HTMLElement | null> } | undefined>(
  undefined
);
export const PageContext = createContext<
  { page: PageType | undefined; page_name: string | undefined; originalPageName?: string } | undefined
>(undefined);

export const SearchCategoriesContext = createContext<boolean>(false);

type MessageProviderProps = {
  children: React.ReactNode;
  message: MessageType;
  page?: PageType; // Optional override if not using message.pages?.[0] - e.g. when editing and there's a pageWithChanges
  originalPageName?: string; // Used to know the original name of the page incase the user has changed it when editing
};

/** Provides the message and page context to all children.
 *
 *  Use like:
 *  ```js
 *   const message = useMessage();
 *  ```
 *
 *  Also provides the audience context, which is used by the Audience component.
 *  You can use the AudienceProvider, PageProvider and MessageIndexProvider
 *  to override the context for a single child.
 *
 *  This is useful, for example, when a message contains multiple pages,
 *  for example when one page include another page.  Note that inner pages
 *  will increment the message index by 0.1 or 0.01.  So floor the messageIndex
 *  to get the real message index.
 *
 *  Note the page can be undefined.  Some messages have no pages.
 */
export const MessageProvider = ({ children, message, page: page_, originalPageName }: MessageProviderProps) => {
  const page = message.is_sent ? undefined : page_ || message.pages?.[0];
  const page_name = page?.name;
  if (message.index === undefined) {
    throw new Error('Missing message index');
  }
  return (
    <MessageContext.Provider value={message}>
      <MessageIndexContext.Provider value={message.index}>
        <PageContext.Provider
          value={page ? { page, page_name: page_name!, originalPageName: originalPageName } : undefined}
        >
          {children}
        </PageContext.Provider>
      </MessageIndexContext.Provider>
    </MessageContext.Provider>
  );
};

export const MessageIndexProvider = MessageIndexContext.Provider;
export const PageProvider = PageContext.Provider;

export const PageProviderByPageName = ({
  children,
  page_name,
  document_version_id,
}: {
  children: React.ReactNode;
  page_name: string;
  document_version_id: string;
}) => {
  const page = useAppSelector((state) =>
    selectPageByDocumentVersionAndTitleWithChanges(state, document_version_id, page_name)
  );
  return <PageProvider value={{ page, page_name }}>{children}</PageProvider>;
};

export const useMessage = () => {
  const message = useContext(MessageContext);
  if (!message) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return message;
};

export const useMessageIndexIfExists = () => {
  const messageIndex = useContext(MessageIndexContext);
  return messageIndex;
};
export const useMessageIndex = () => {
  const messageIndex = useContext(MessageIndexContext);
  if (messageIndex === undefined) {
    throw new Error('useMessageIndex must be used within a MessageIndexProvider');
  }
  return messageIndex;
};

export const useFhirScope = () => {
  const fhirScope = useContext(FhirScopeContext);
  if (!fhirScope) {
    throw new Error('useFhirScope must be used within a FhirScopeContext.Provider');
  }
  return fhirScope;
};

export const useFhirScopeIfExists = () => useContext(FhirScopeContext);

export const usePageId = () => {
  const page = useContext(PageContext);
  return page?.page?.id;
};

/** Can return undefined */
export const usePageIfExists = () => useContext(PageContext);

/** Will throw an exception if undefined */
export const usePage = () => {
  const page = useContext(PageContext);
  if (!page) {
    throw new Error('usePage must be used within a PageProvider');
  }
  return page;
};
